import React, { useState } from "react";
import { Modal, Checkbox, Button, Tag } from "antd";
import CWKOTPrinter from "../../../../lib/printer/CWKotPrinter";
import ItemNotesDrawer from "./ItemNotesDrawer";

const CancelOrderItemsModal = ({
  cancelKotItems,
  setCancelKotItems,
  cart,
  selectedItemIds,
  setSelectedItemIds,
  groupedKotItems,
  toggleAll,
  toggleKot,
  toggleItem,
  currenciesList,
  handleVoid,
  selectedTable,
  filteredCartObj,
  loading,
  setLoading,
  posConfig,
  lineRemarks,
  setLineRemarks,
}) => {
  const requireRemarks = posConfig?.requireRemarksForKOTCancellation === "Y";

  const [remarkDrawerOpen, setRemarkDrawerOpen] = useState(false);
  const [remarkTarget, setRemarkTarget] = useState(null);
  const [remarkValue, setRemarkValue] = useState("");

  const openRemarkDrawer = (item) => {
    setRemarkTarget(item);
    setRemarkValue(lineRemarks?.[item.fbOrderLineId] || "");
    setRemarkDrawerOpen(true);
  };

  const handleRemarkConfirm = (value) => {
    if (remarkTarget) {
      setLineRemarks((prev) => ({ ...prev, [remarkTarget.fbOrderLineId]: value }));
    }
    setRemarkDrawerOpen(false);
    setRemarkTarget(null);
    setRemarkValue("");
  };

  const handleToggleItem = (item) => {
    const isCurrentlySelected = selectedItemIds.includes(item.fbOrderLineId);
    toggleItem(item.fbOrderLineId);
    if (!isCurrentlySelected && requireRemarks) {
      openRemarkDrawer(item);
    }
  };

  const missingRemarks = requireRemarks ? selectedItemIds.filter((id) => !lineRemarks?.[id]?.trim()) : [];
  const canCancelSelected = selectedItemIds.length > 0 && missingRemarks.length === 0;

  const allCancellableIds = (cart?.items?.filter((item) => !["RC", "CO", "CL"].includes(item.orderStatus)) || []).map((item) => item.fbOrderLineId);

  const statusTag = (orderStatus) => {
    const map = {
      OR: { color: "blue", label: "Order Received" },
      IP: { color: "orange", label: "In Progress" },
      RC: { color: "green", label: "Ready to Collect" },
      CO: { color: "purple", label: "Completed" },
      CL: { color: "red", label: "Cancelled" },
    };
    const entry = map[orderStatus] || { color: "default", label: "" };
    return (
      <Tag color={entry.color} style={{ borderRadius: "4px", padding: "1px 3px" }}>
        {entry.label}
      </Tag>
    );
  };

  return (
    <>
      <Modal open={cancelKotItems} onCancel={() => setCancelKotItems(false)} footer={null} width={450} centered>
        <div style={{ fontSize: 18, fontWeight: 600 }}>Cancel Order Items</div>
        <div style={{ fontSize: 13, color: "#777", marginBottom: 16 }}>Select items you want to cancel. Items are grouped by KOT token.</div>

        {/* SELECT ALL */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
          <Checkbox checked={cart?.items?.length > 0 && selectedItemIds.length === cart.items.length} onChange={(e) => toggleAll(e.target.checked)}>
            Select All Items
          </Checkbox>
          <span style={{ fontSize: 12, color: "#888" }}>
            {selectedItemIds.length} of {cart?.items?.length || 0} selected
          </span>
        </div>

        {/* LIST */}
        <div style={{ maxHeight: 280, overflowY: "auto" }}>
          {Object.entries(groupedKotItems || {})
            .map(([kotId, items]) => {
              const activeItems = items.filter((item) => item.isCanceled !== "Y");
              if (activeItems.length === 0) return null;
              return [kotId, activeItems];
            })
            .filter(Boolean)
            .map(([kotId, items], index) => {
              const cancellableItems = items.filter((item) => item.orderStatus === "OR" || item.orderStatus === "IP");
              const isKotCancellable = cancellableItems.length > 0;
              const allKotSelected = isKotCancellable && cancellableItems.every((i) => selectedItemIds.includes(i.fbOrderLineId));

              return (
                <div
                  key={kotId}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 12,
                    opacity: isKotCancellable ? 1 : 0.45,
                  }}
                >
                  {/* KOT HEADER */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <Checkbox
                      checked={allKotSelected}
                      disabled={!isKotCancellable}
                      onChange={(e) => {
                        if (!isKotCancellable) return;
                        toggleKot(cancellableItems, e.target.checked);
                      }}
                    />
                    <b>KOT #{index + 1}</b>
                    <span style={{ fontSize: 12, color: "#888" }}>{items.length} items</span>
                  </div>

                  {/* ITEMS */}
                  {items.map((item) => {
                    const isCancellable = item.orderStatus === "OR" || item.orderStatus === "IP";
                    const isSelected = selectedItemIds.includes(item.fbOrderLineId);
                    const remark = lineRemarks?.[item.fbOrderLineId];

                    return (
                      <div
                        key={item.lineId}
                        style={{
                          padding: "8px 0",
                          borderTop: "1px solid #f0f0f0",
                          opacity: isCancellable ? 1 : 0.45,
                          pointerEvents: isCancellable ? "auto" : "none",
                          cursor: isCancellable ? "default" : "not-allowed",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <Checkbox
                            checked={isSelected}
                            disabled={!isCancellable}
                            onChange={() => {
                              if (!isCancellable) return;
                              handleToggleItem(item);
                            }}
                          />

                          <div style={{ flex: 1 }}>
                            <div>{item.name}</div>
                            <div style={{ fontSize: 12, color: "#888" }}>Qty: {item.weight}</div>
                            {/* Remark display — same style as notes in sidebar */}
                            {remark ? (
                              <span
                                onClick={() => isCancellable && openRemarkDrawer(item)}
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#6B7280",
                                  cursor: isCancellable ? "pointer" : "default",
                                  display: "block",
                                  marginTop: 2,
                                }}
                                title={remark}
                              >
                                📝 {remark}
                              </span>
                            ) : requireRemarks && isSelected ? (
                              <span
                                onClick={() => openRemarkDrawer(item)}
                                style={{ fontSize: "0.75rem", color: "#ef4444", cursor: "pointer", display: "block", marginTop: 2 }}
                              >
                                * Add remark to cancel
                              </span>
                            ) : null}
                          </div>

                          <div style={{ width: 90, textAlign: "right" }}>
                            {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                            {Number(item.linegross || item.nettotal).toFixed(2)}
                            {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol}` : ""}
                          </div>

                          {statusTag(item.orderStatus)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>

        {/* FOOTER */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
          <Button onClick={() => setCancelKotItems(false)}>Close</Button>

          <div style={{ display: "flex", gap: 10 }}>
            <Button
              disabled={!canCancelSelected}
              title={requireRemarks && missingRemarks.length > 0 ? "Add remarks for all selected items" : ""}
              onClick={() => {
                setCancelKotItems(false);
                handleVoid(selectedTable, selectedItemIds, lineRemarks);
                // Enrich items with their per-line remarks before printing
                const enrichedCartObj = {
                  ...filteredCartObj,
                  items: (filteredCartObj.items || []).map((item) => ({
                    ...item,
                    remarks: lineRemarks?.[item.fbOrderLineId] || item.remarks || "",
                  })),
                  remarks: [...new Set(
                    (filteredCartObj.items || [])
                      .map((item) => lineRemarks?.[item.fbOrderLineId])
                      .filter(Boolean)
                  )].join(", "),
                };
                CWKOTPrinter(enrichedCartObj, false, true);
              }}
            >
              Cancel Selected ({selectedItemIds.length})
            </Button>
            <Button
              type="primary"
              style={{ background: "#2F3856" }}
              onClick={async () => {
                if (!allCancellableIds.length) {
                  console.warn("No cancellable items found. Cancel aborted.");
                  return;
                }
                try {
                  setLoading(true);
                  await handleVoid(selectedTable, allCancellableIds, lineRemarks);
                  setCancelKotItems(false);
                } catch (error) {
                  console.error("Error cancelling items:", error);
                } finally {
                  // setLoading(false);
                }
              }}
            >
              Cancel All
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remarks drawer — reuses the same ItemNotesDrawer UI */}
      <ItemNotesDrawer
        notesPopup={remarkDrawerOpen}
        setNotesPopup={setRemarkDrawerOpen}
        notesValue={remarkValue}
        setNotesValue={setRemarkValue}
        title="Add Cancellation Remark"
        onConfirm={handleRemarkConfirm}
      />
    </>
  );
};

export default CancelOrderItemsModal;
